// src/pages/Office/WhatsAppManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Send, Phone, User, Clock, CheckCheck, MessageCircle, Search, 
  Plus, Settings, RefreshCw, Loader2, X, Reply, Pin, Archive 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import WhatsAppService from "../../lib/whatsappService"
import { useAuth } from '../../contexts/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const WhatsAppManager = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const messagesEndRef = useRef(null);

  // Fetch all conversations
  const { data: conversations = [], isLoading: convLoading, refetch: refetchConversations } = useQuery({
    queryKey: ['whatsapp-conversations'],
    queryFn: async () => {
      try {
        return await WhatsAppService.getConversations();
      } catch (error) {
        console.error('Error fetching conversations:', error);
        toast.error('Failed to load conversations');
        return [];
      }
    },
    refetchInterval: 10000, // Refresh every 10 seconds
    enabled: !!currentUser
  });

  // Fetch templates
  const { data: templates = [] } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => pb.collection('whatsapp_templates').getFullList({ sort: 'name' }),
    enabled: !!currentUser
  });

  // Fetch messages for selected conversation
  const { data: messages = [], refetch: refetchMessages, isLoading: msgsLoading } = useQuery({
    queryKey: ['whatsapp-messages', selectedConversation?.phone_number],
    queryFn: async () => {
      if (!selectedConversation) return [];
      try {
        const msgs = await pb.collection('whatsapp_messages').getFullList({
          filter: `(from_number="${selectedConversation.phone_number}" || to_number="${selectedConversation.phone_number}")`,
          sort: 'created',
          expand: 'customer_id'
        });
        
        // Mark unread messages as read
        for (const msg of msgs) {
          if (!msg.is_read && msg.direction === 'incoming') {
            await pb.collection('whatsapp_messages').update(msg.id, { is_read: true });
          }
        }
        
        return msgs;
      } catch (error) {
        console.error('Error fetching messages:', error);
        return [];
      }
    },
    enabled: !!selectedConversation
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      if (!messageText.trim()) throw new Error('Message cannot be empty');
      if (!selectedConversation) throw new Error('No conversation selected');
      
      return await WhatsAppService.sendTextMessage(
        selectedConversation.phone_number,
        messageText,
        selectedConversation.customer_id
      );
    },
    onSuccess: () => {
      setMessageText('');
      queryClient.invalidateQueries(['whatsapp-conversations']);
      refetchMessages();
      toast.success('Message sent successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send message');
    }
  });

  // Send template message mutation
  const sendTemplateMutation = useMutation({
    mutationFn: async ({ templateName, variables }) => {
      if (!selectedConversation) throw new Error('No conversation selected');
      return await WhatsAppService.sendTemplateMessage(
        selectedConversation.phone_number,
        templateName,
        variables,
        selectedConversation.customer_id
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsapp-conversations']);
      refetchMessages();
      setTemplateDialogOpen(false);
      setSelectedTemplate(null);
      setTemplateVariables([]);
      toast.success('Template message sent successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send template message');
    }
  });

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  // Format timestamp
  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), 'hh:mm a');
    } catch {
      return '';
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return '';
    }
  };

  const getTimeAgo = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      'sent': 'bg-blue-100 text-blue-800',
      'delivered': 'bg-green-100 text-green-800',
      'read': 'bg-emerald-100 text-emerald-800',
      'failed': 'bg-red-100 text-red-800',
      'received': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = 
      conv.phone_number?.includes(searchTerm) ||
      conv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'unread' && conv.unread_count > 0);
    return matchesSearch && statusMatch;
  });

  if (convLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-2 text-slate-500">Loading conversations...</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4">
      {/* Conversations List */}
      <Card className="w-96 flex flex-col shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-indigo-600" />
              Conversations
            </CardTitle>
            <Badge variant="outline" className="text-xs">
              {conversations.length} chats
            </Badge>
          </div>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by phone or name..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2 mt-2">
            <Button 
              variant={filterStatus === 'all' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilterStatus('all')}
              className="flex-1 text-xs"
            >
              All
            </Button>
            <Button 
              variant={filterStatus === 'unread' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilterStatus('unread')}
              className="flex-1 text-xs"
            >
              <span className="flex items-center gap-1">
                Unread
                {conversations.filter(c => c.unread_count > 0).length > 0 && (
                  <Badge className="bg-red-500 text-white text-xs px-1.5">
                    {conversations.filter(c => c.unread_count > 0).length}
                  </Badge>
                )}
              </span>
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => refetchConversations()}
              className="h-9 w-9"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 p-0">
          <ScrollArea className="h-[calc(100vh-280px)]">
            {filteredConversations.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No conversations found</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`p-3 border-b cursor-pointer hover:bg-slate-50 transition-all duration-150 ${
                    selectedConversation?.id === conv.id ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                  }`}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-indigo-100 text-indigo-700">
                        {getInitials(conv.customer_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <p className="font-semibold truncate">{conv.customer_name}</p>
                        <span className="text-xs text-slate-400 shrink-0 ml-2">
                          {getTimeAgo(conv.last_message_time)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 font-mono text-xs">{conv.phone_number}</p>
                      <p className="text-xs text-slate-400 truncate mt-1">
                        {conv.last_message?.slice(0, 50)}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-indigo-600 text-white rounded-full px-2 py-0.5 text-xs shrink-0 mt-1">
                        {conv.unread_count}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col shadow-sm">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <CardHeader className="border-b pb-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-indigo-100 text-indigo-700">
                      {getInitials(selectedConversation.customer_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{selectedConversation.customer_name}</p>
                    <p className="text-xs text-slate-500 font-mono">{selectedConversation.phone_number}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {templates.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setTemplateDialogOpen(true);
                        setTemplateVariables([]);
                        setSelectedTemplate(null);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Template
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      window.open(`https://wa.me/${selectedConversation.phone_number}`, '_blank');
                    }}
                  >
                    <Phone className="w-4 h-4 mr-1" /> WhatsApp
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      // Navigate to customer profile
                      if (selectedConversation.customer_id) {
                        // Handle navigation to customer
                      }
                    }}
                  >
                    <User className="w-4 h-4 mr-1" /> Profile
                  </Button>
                </div>
              </div>
            </CardHeader>

            {/* Messages */}
            <CardContent className="flex-1 overflow-y-auto p-4">
              {msgsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-slate-400 py-10">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-xs">Send a message to start the conversation</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === 'incoming' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                          msg.direction === 'incoming'
                            ? 'bg-slate-100 text-slate-800'
                            : 'bg-indigo-600 text-white'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message_body}</p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${
                          msg.direction === 'incoming' ? 'text-slate-400' : 'text-indigo-200'
                        }`}>
                          <span>{formatTime(msg.created)}</span>
                          {msg.direction === 'outgoing' && msg.status === 'delivered' && (
                            <CheckCheck className="w-3 h-3" />
                          )}
                          {msg.direction === 'outgoing' && msg.status === 'read' && (
                            <CheckCheck className="w-3 h-3 text-blue-300" />
                          )}
                          {msg.status && msg.direction === 'incoming' && (
                            <Badge className={`${getStatusColor(msg.status)} text-xs px-1.5 py-0`}>
                              {msg.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </CardContent>

            {/* Message Input */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Type your message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (messageText.trim()) {
                        sendMessageMutation.mutate();
                      }
                    }
                  }}
                  className="resize-none min-h-[50px] max-h-[120px]"
                  rows={2}
                  disabled={sendMessageMutation.isPending}
                />
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => sendMessageMutation.mutate()}
                    disabled={!messageText.trim() || sendMessageMutation.isPending}
                    className="h-12 w-12 shrink-0"
                    size="icon"
                  >
                    {sendMessageMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium text-slate-600">No conversation selected</p>
              <p className="text-sm">Choose a conversation from the list to start chatting</p>
            </div>
          </div>
        )}
      </Card>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-indigo-600" />
              Send Template Message
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Template</Label>
              <Select
                value={selectedTemplate?.id || ''}
                onValueChange={(value) => {
                  const template = templates.find(t => t.id === value);
                  setSelectedTemplate(template);
                  // Initialize variables array based on template placeholders
                  if (template) {
                    const placeholderCount = (template.body.match(/\{\{\d+\}\}/g) || []).length;
                    setTemplateVariables(Array(placeholderCount).fill(''));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="flex items-center gap-2">
                        {template.name}
                        <Badge variant="outline" className="text-xs">
                          {template.category}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500">Template Preview</Label>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{selectedTemplate.body}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {templateVariables.filter(v => v).length} / {templateVariables.length} variables filled
                  </p>
                </div>

                {templateVariables.length > 0 && (
                  <div>
                    <Label>Variables</Label>
                    <div className="space-y-2 mt-1">
                      {templateVariables.map((value, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-500 w-16">
                            {index + 1}:
                          </span>
                          <Input
                            placeholder={`Enter value for variable ${index + 1}`}
                            value={value}
                            onChange={(e) => {
                              const newVars = [...templateVariables];
                              newVars[index] = e.target.value;
                              setTemplateVariables(newVars);
                            }}
                            className="flex-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!selectedTemplate) {
                  toast.error('Please select a template');
                  return;
                }
                if (templateVariables.some(v => !v)) {
                  toast.error('Please fill all variables');
                  return;
                }
                sendTemplateMutation.mutate({
                  templateName: selectedTemplate.name,
                  variables: templateVariables
                });
              }}
              disabled={!selectedTemplate || sendTemplateMutation.isPending}
            >
              {sendTemplateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                </>
              ) : (
                'Send Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppManager;